<template>
    <div class="opc-curve-chart">
        <div ref="chart" class="opc-curve-chart__canvas"></div>
        <div v-if="emptyText" class="opc-curve-chart__empty text-muted">{{ emptyText }}</div>
    </div>
</template>

<script>
/* eslint-env browser */
// OPC 点位多曲线（echarts 5）。生命周期照 SapHandleRecordStatPanel：ref 容器、window resize、beforeDestroy dispose、
// 0 尺寸容器（卡片还没布局完 / 在折叠区里）时先不建，等下一次渲染或 resize 补建。
//
// 契约（S2 / S3 依赖，名字不得改）：
//   props  series   [{ nodeId, eocId, points: [[epochMillis, value], ...], name, color, unit? }]
//          range    [Date, Date]  查询时间范围（横轴范围）
//          settings { span, spanUnit, ymin, ymax, yunit, xinterval }（yunit 不直接用，纵轴名称看 axisUnit）
//          axisUnit 纵轴名称（页面按「手动单位 / 勾选点位的共同单位」算好）；悬停提示里每条曲线优先显示自己的 unit
//   events view-change [startMs, endMs]  缩放 / 平移 / 重画停下 300 ms 后的视窗（页面据此补取更细的数据）
//          x-interval-fallback boolean   设了刻度间隔但当前视窗刻度过多、已按默认刻度显示
//   methods resetView()      回到初始视窗
//           getViewImage()   Promise<{ dataUrl, width, height }>：当前视图（含图例与纵轴单位）的 PNG
//           getViewImage({width, height, pixelRatio?})  同上，但按给定尺寸离屏重画当前视图（导出 PDF 用）：
//                            图例改为平铺换行（不是可滚动的单行，打印出来能看到全部点位），保留图例的显隐选择
//
// 历史值只在变化时记录（不是等间隔采样），悬停提示显示的是各点位「该时刻最近一次记录的值」。
// 后端已在每条序列前补了「带入点」（start 之前最后一个值，时间记为 start），并用最后一个值延伸到 min(end, 当前时刻)，
// 所以区间开始前就变化过、区间内没再变的点位也是一条贯穿区间的水平线，区间内任意时刻悬停都有值；
// 只有截至 end 从没记录过的点位才没有点（页面据此显示「没有数据」）。
//
// 横轴刻度间隔（settings.xinterval）：echarts 5.3 的时间轴只会从内置间隔表里挑（设 6 小时出来是 4 小时），数值轴的 interval
// 又从视窗左端起算、平移时网格跟着漂。所以时间轴照旧负责缩放与悬停，只隐藏它的刻度与标签，另用一个空系列的 markLine 画竖线与标签：
// 刻度锚定在查询开始那天的 0 点，每次缩放重算；视窗内超过 MAX_X_TICKS 条时退回默认刻度。
import * as echarts from 'echarts';
import {initialWindow, toNumberOrNull, xIntervalMs, yRangeInvalid} from "./opcCurveSettings";

function pad(n) {
    return n < 10 ? '0' + n : '' + n;
}

function formatTime(ms, withSeconds) {
    const d = new Date(ms);
    const text = d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate())
        + ' ' + pad(d.getHours()) + ':' + pad(d.getMinutes());
    return withSeconds ? text + ':' + pad(d.getSeconds()) : text;
}

// points 按时间升序：取 ≤ time 的最后一个点（该时刻生效的值）
function valueAt(points, time) {
    let lo = 0;
    let hi = points.length - 1;
    let found = -1;
    while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (points[mid][0] <= time) {
            found = mid;
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }
    return found === -1 ? null : points[found][1];
}

function formatValue(value) {
    if (value === null || value === undefined) {
        return '-';
    }
    return Math.abs(value) >= 1e6 || Number.isInteger(value) ? String(value) : String(Number(value.toFixed(4)));
}

// 打印用的平铺图例：图标宽 25 + 图标文字间距 5 + 文字宽；项间距与换行行距都是 itemGap（echarts box 布局）
const LEGEND_GAP = 10;
const LEGEND_ITEM_HEIGHT = 14;

// 按文字宽度估算平铺图例在 width 内要占几行（echarts 不提供图例高度的查询，给 grid 让位只能估算）
function legendRows(names, width, measure) {
    const maxWidth = width - 10;
    let rows = 1;
    let x = 0;
    names.forEach(name => {
        const itemWidth = 25 + 5 + measure(name);
        if (x > 0 && x + itemWidth > maxWidth) {
            rows++;
            x = 0;
        }
        x += itemWidth + LEGEND_GAP;
    });
    return rows;
}

// echarts 默认字体 12px sans-serif 下的文字宽度
function textMeasurer() {
    const ctx = document.createElement('canvas').getContext('2d');
    if (!ctx) {
        return text => String(text).length * 12;
    }
    ctx.font = '12px sans-serif';
    return text => ctx.measureText(String(text)).width;
}

const X_GRID_ID = '__opc_curve_x_grid__';
const MAX_X_TICKS = 200;
// 自定义刻度标签之间的最小像素间距（两行「MM-DD / HH:mm」约 40px 宽；再密就隔几条显示一个标签，竖线照画）
const X_LABEL_MIN_GAP = 48;

// [from, to] 内从 anchor 起每 interval 一条的刻度；超过 MAX_X_TICKS 返回 null
function xTicks(view, anchor, interval) {
    const first = anchor + Math.ceil((view[0] - anchor) / interval) * interval;
    if ((view[1] - first) / interval + 1 > MAX_X_TICKS) {
        return null;
    }
    const ticks = [];
    for (let t = first; t <= view[1]; t += interval) {
        ticks.push(t);
    }
    return ticks;
}

function escapeHtml(text) {
    return String(text).replace(/[&<>"']/g, ch => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[ch]));
}

export default {
    name: "OpcCurveChart",
    props: {
        series: {
            type: Array,
            default: () => []
        },
        range: {
            type: Array,
            default: null
        },
        settings: {
            type: Object,
            required: true
        },
        // 无曲线时的占位文字（由页面决定：未勾选 / 无数据）
        placeholder: {
            type: String,
            default: ''
        },
        axisUnit: {
            type: String,
            default: ''
        }
    },
    computed: {
        hasData() {
            return this.series.some(s => s.points && s.points.length);
        },
        emptyText() {
            return this.hasData ? '' : this.placeholder;
        },
        // 视窗（range + 跨度）变了才回到初始视窗；只是勾选 / 纵轴设置变了则保留用户当前的缩放
        viewKey() {
            if (!this.validRange) {
                return '';
            }
            return initialWindow(this.range, this.settings).join('|');
        },
        validRange() {
            return Array.isArray(this.range) && this.range.length === 2
                && this.range[0] instanceof Date && this.range[1] instanceof Date
                && this.range[0].getTime() < this.range[1].getTime();
        }
    },
    watch: {
        series() {
            this.render();
        },
        range() {
            this.render();
        },
        settings: {
            deep: true,
            handler() {
                this.render();
            }
        },
        axisUnit() {
            this.render();
        }
    },
    created() {
        // 非响应式：echarts 实例、上次应用的视窗 key、自定义刻度是否生效、缩放事件的节流 / 防抖句柄
        this.chart = null;
        this.appliedViewKey = null;
        this.xGridCustom = false;
        this.xGridFallback = false;
        this.xGridFrame = null;
        this.viewTimer = null;
    },
    mounted() {
        window.addEventListener('resize', this.resize);
        this.$nextTick(this.render);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.resize);
        if (this.xGridFrame) {
            cancelAnimationFrame(this.xGridFrame);
        }
        clearTimeout(this.viewTimer);
        if (this.chart) {
            this.chart.dispose();
            this.chart = null;
        }
    },
    methods: {
        ensureChart() {
            if (this.chart) {
                return this.chart;
            }
            const el = this.$refs.chart;
            if (!el || !el.offsetWidth || !el.offsetHeight) {
                return null;
            }
            this.chart = echarts.init(el);
            this.chart.getZr().on('dblclick', this.resetView);
            this.chart.on('datazoom', this.onDataZoom);
            return this.chart;
        },
        resize() {
            if (!this.chart) {
                this.render();
                return;
            }
            this.chart.resize();
        },
        // 当前视窗 [startValue, endValue]（横轴 min/max 固定为查询范围，百分比可换算）
        currentWindow() {
            if (!this.chart) {
                return null;
            }
            const option = this.chart.getOption();
            const zoom = option && option.dataZoom && option.dataZoom[0];
            if (!zoom) {
                return null;
            }
            if (typeof zoom.startValue === 'number' && typeof zoom.endValue === 'number') {
                return [zoom.startValue, zoom.endValue];
            }
            if (typeof zoom.start === 'number' && typeof zoom.end === 'number' && this.validRange) {
                const min = this.range[0].getTime();
                const width = this.range[1].getTime() - min;
                return [min + width * zoom.start / 100, min + width * zoom.end / 100];
            }
            return null;
        },
        render() {
            const chart = this.ensureChart();
            if (!chart) {
                return;
            }
            if (!this.validRange) {
                chart.clear();
                this.appliedViewKey = null;
                return;
            }
            let view = null;
            if (this.appliedViewKey === this.viewKey) {
                view = this.currentWindow();
            }
            if (!view) {
                view = initialWindow(this.range, this.settings);
            }
            chart.setOption(this.buildOption(view), {notMerge: true});
            this.appliedViewKey = this.viewKey;
            const ticks = this.customTicks(view);
            this.xGridCustom = !!ticks;
            this.setFallback(xIntervalMs(this.settings) !== null && !ticks);
            // 新范围 / 新数据后也报一次视窗：页面据此判断要不要补细节（已有细节够用时它不会重复请求）
            this.scheduleViewChange();
        },
        setFallback(fallback) {
            if (fallback !== this.xGridFallback) {
                this.xGridFallback = fallback;
                this.$emit('x-interval-fallback', fallback);
            }
        },
        // 缩放 / 平移：每帧至多重算一次自定义刻度；停下 300 ms 后把视窗告诉页面
        onDataZoom() {
            if (!this.xGridFrame) {
                this.xGridFrame = requestAnimationFrame(() => {
                    this.xGridFrame = null;
                    this.refreshXGrid();
                });
            }
            this.scheduleViewChange();
        },
        scheduleViewChange() {
            clearTimeout(this.viewTimer);
            this.viewTimer = setTimeout(() => {
                const view = this.currentWindow();
                if (view) {
                    this.$emit('view-change', view);
                }
            }, 300);
        },
        refreshXGrid() {
            if (!this.chart || !this.validRange || xIntervalMs(this.settings) === null) {
                return;
            }
            const view = this.currentWindow();
            if (!view) {
                return;
            }
            const ticks = this.customTicks(view);
            if (!!ticks !== this.xGridCustom) {
                // 自定义刻度与默认刻度互换要改坐标轴，整张重画（视窗保留）
                this.render();
                return;
            }
            if (ticks) {
                this.chart.setOption({series: [{id: X_GRID_ID, markLine: {data: this.xGridData(ticks, this.chart.getWidth())}}]});
            }
        },
        // 当前设置下视窗内的自定义刻度；没设间隔或刻度过多返回 null
        customTicks(view) {
            const interval = xIntervalMs(this.settings);
            if (interval === null || !this.validRange) {
                return null;
            }
            const anchor = new Date(this.range[0].getTime()).setHours(0, 0, 0, 0);
            return xTicks(view, anchor, interval);
        },
        // markLine 数据：竖线全画，标签按图宽抽稀
        xGridData(ticks, width) {
            const plotWidth = Math.max(100, width - 80);
            const step = Math.max(1, Math.ceil(X_LABEL_MIN_GAP * ticks.length / plotWidth));
            return ticks.map((t, i) => ({xAxis: t, label: {show: i % step === 0}}));
        },
        xGridSeries(ticks, width) {
            return {
                id: X_GRID_ID,
                type: 'line',
                data: [],
                silent: true,
                markLine: {
                    symbol: 'none',
                    silent: true,
                    animation: false,
                    lineStyle: {color: '#E0E6F1', type: 'solid', width: 1},
                    label: {
                        position: 'start',
                        color: '#6E7079',
                        lineHeight: 14,
                        formatter: params => {
                            const text = formatTime(params.value, false);
                            return text.slice(5, 10) + '\n' + text.slice(11);
                        }
                    },
                    emphasis: {disabled: true},
                    data: this.xGridData(ticks, width)
                }
            };
        },
        /**
         * @param view  [startValue, endValue] 横轴视窗
         * @param print 离屏打印时的图例：{ gridTop, selected, width }；不传 = 页面上的交互图表
         */
        buildOption(view, print) {
            const invalid = yRangeInvalid(this.settings);
            const ymin = invalid ? null : toNumberOrNull(this.settings.ymin);
            const ymax = invalid ? null : toNumberOrNull(this.settings.ymax);
            const series = this.series;
            const names = series.map(s => s.name);
            const ticks = this.customTicks(view);
            const lines = series.map(s => ({
                id: s.nodeId,
                name: s.name,
                type: 'line',
                data: s.points || [],
                showSymbol: false,
                symbolSize: 4,
                lineStyle: {width: 1.5},
                itemStyle: {color: s.color},
                emphasis: {disabled: true}
            }));
            if (ticks) {
                const width = print ? print.width : (this.chart ? this.chart.getWidth() : 800);
                lines.push(this.xGridSeries(ticks, width));
            }
            return {
                animation: false,
                legend: print
                    ? {top: 0, left: 'center', type: 'plain', itemGap: LEGEND_GAP, data: names, selected: print.selected}
                    : {top: 0, type: 'scroll', data: names},
                // 自定义刻度的标签画在绘图区下方两行，containLabel 算不到它，底部要自己留
                grid: {top: print ? print.gridTop : 36, left: 16, right: 24, bottom: ticks ? 40 : 16, containLabel: true},
                tooltip: {
                    trigger: 'axis',
                    axisPointer: {type: 'line', snap: false},
                    confine: true,
                    formatter: params => this.tooltip(params)
                },
                xAxis: {
                    type: 'time',
                    min: this.range[0].getTime(),
                    max: this.range[1].getTime(),
                    axisLabel: {show: !ticks, hideOverlap: true},
                    axisTick: {show: !ticks},
                    splitLine: {show: false}
                },
                yAxis: {
                    type: 'value',
                    name: this.axisUnit || '',
                    nameLocation: 'end',
                    nameTextStyle: {align: 'left'},
                    // min/max 为 null 时 echarts 自动取范围；scale 让自动范围不强制包含 0
                    min: ymin,
                    max: ymax,
                    scale: true
                },
                dataZoom: [{
                    type: 'inside',
                    xAxisIndex: 0,
                    filterMode: 'none',
                    zoomOnMouseWheel: true,
                    moveOnMouseMove: true,
                    moveOnMouseWheel: false,
                    startValue: view[0],
                    endValue: view[1]
                }],
                series: lines
            };
        },
        tooltip(params) {
            const list = Array.isArray(params) ? params : [params];
            if (!list.length) {
                return '';
            }
            const time = list[0].axisValue;
            const rows = this.series.map(s => {
                const value = valueAt(s.points || [], time);
                const unit = s.unit || this.axisUnit;
                return '<div><span style="display:inline-block;width:10px;height:10px;border-radius:50%;margin-right:6px;background:'
                    + s.color + '"></span>' + escapeHtml(s.name) + '：<b>' + formatValue(value) + '</b>'
                    + (unit ? ' ' + escapeHtml(unit) : '') + '</div>';
            });
            return '<div>' + formatTime(time, true) + '</div>' + rows.join('');
        },
        resetView() {
            if (!this.chart || !this.validRange) {
                return;
            }
            const view = initialWindow(this.range, this.settings);
            this.chart.dispatchAction({type: 'dataZoom', dataZoomIndex: 0, startValue: view[0], endValue: view[1]});
        },
        /**
         * 当前视图（含图例与纵轴单位）的 PNG。
         * @param size 可选 {width, height, pixelRatio}：按该尺寸离屏重画当前视窗（导出 PDF 用，版面尺寸固定、图例平铺）；
         *             不传则直接截页面上的图表
         */
        getViewImage(size) {
            return new Promise((resolve, reject) => {
                const chart = this.ensureChart();
                if (!chart) {
                    reject(new Error('chart not ready'));
                    return;
                }
                const width = size ? Math.round(Number(size.width)) : 0;
                const height = size ? Math.round(Number(size.height)) : 0;
                if (!(width > 0 && height > 0) || !this.validRange) {
                    resolve({
                        dataUrl: chart.getDataURL({type: 'png', pixelRatio: 2, backgroundColor: '#fff'}),
                        width: chart.getWidth(),
                        height: chart.getHeight()
                    });
                    return;
                }
                try {
                    resolve(this.renderOffscreen(chart, width, height, Number(size.pixelRatio) || 2));
                } catch (e) {
                    reject(e);
                }
            });
        },
        // 用页面图表的当前视窗与图例显隐，在给定尺寸的离屏容器里重画一张图并截成 PNG
        renderOffscreen(chart, width, height, pixelRatio) {
            const view = this.currentWindow() || initialWindow(this.range, this.settings);
            const option = chart.getOption();
            const legend = option && option.legend && option.legend[0];
            const names = this.series.map(s => s.name);
            const rows = names.length ? legendRows(names, width, textMeasurer()) : 0;
            // 图例行 + 纵轴名称（单位）在绘图区上方
            const gridTop = 8 + rows * (LEGEND_ITEM_HEIGHT + LEGEND_GAP) + (this.axisUnit ? 16 : 0);
            const host = document.createElement('div');
            host.style.cssText = 'position:fixed;left:-20000px;top:0;width:' + width + 'px;height:' + height + 'px;';
            document.body.appendChild(host);
            let offscreen = null;
            try {
                offscreen = echarts.init(host, null, {width, height});
                offscreen.setOption(this.buildOption(view, {
                    gridTop: Math.max(gridTop, 24),
                    width,
                    selected: legend && legend.selected ? Object.assign({}, legend.selected) : undefined
                }), {notMerge: true});
                return {
                    dataUrl: offscreen.getDataURL({type: 'png', pixelRatio, backgroundColor: '#fff'}),
                    width,
                    height
                };
            } finally {
                if (offscreen) {
                    offscreen.dispose();
                }
                document.body.removeChild(host);
            }
        }
    }
};
</script>

<style scoped>
.opc-curve-chart {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 320px;
}

.opc-curve-chart__canvas {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
}

.opc-curve-chart__empty {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    text-align: center;
    transform: translateY(-50%);
    pointer-events: none;
}
</style>
